import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Subject } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { environment } from "@env/environment";

export interface Language {
  code: string;
  label: string;
  flag: string;
  languageId: number;
}

export const SUPPORTED_LANGUAGES: Language[] = [
  { code: "es", label: "Español", flag: "🇪🇸", languageId: 1 },
  { code: "pt", label: "Português", flag: "🇧🇷", languageId: 4 },
];

const LANG_STORAGE_KEY = "affiliate_lang";

interface CountryEntry {
  id: number;
  language?: string;
  [key: string]: any;
}

@Injectable({ providedIn: "root" })
export class LanguageService {
  public languages = SUPPORTED_LANGUAGES;
  public currentLang!: Language | any;
  public languageChange$ = new Subject<Language>();

  constructor(
    private _translate: TranslateService,
    private _http: HttpClient
  ) {
    this._translate.addLangs(this.languages.map((l) => l.code));
  }

  get currentLanguageId(): number {
    return this.currentLang?.languageId ?? 1;
  }

  get langBaseDomain(): string {
    return this.currentLang?.code === 'pt'
      ? 'https://www.edutin.com/br'
      : 'https://www.edutin.com';
  }

  init(): void {
    const saved = localStorage.getItem(LANG_STORAGE_KEY);

    if (saved) {
      this.setLanguage(saved, false);
      return;
    }

    this.setLanguage("es", false);
    this.detectLanguageByLocation();
  }

  setLanguage(code: string, persist = true): void {
    if (!this.isSupported(code)) return;
    this.currentLang = this.languages.find((l) => l.code === code);
    if (persist) {
      localStorage.setItem(LANG_STORAGE_KEY, code);
    }
    this._translate.use(code).subscribe(() => {
      if (persist) {
        this.languageChange$.next(this.currentLang);
      }
    });
  }

  private detectLanguageByLocation(): void {
    this._http.get<any>(environment.urlUserLocation).subscribe({
      next: (res) => {
        const countryId = res?.countryData?.bd_country_id;
        if (!countryId) {
          this.setLanguage("es");
          return;
        }

        this._http.get<CountryEntry[]>("assets/countries.json").subscribe({
          next: (countries) => {
            const country = countries.find(
              (c) => String(c.id) === String(countryId)
            );
            const langCode = country?.language && this.isSupported(country.language)
              ? country.language
              : "es";
            this.setLanguage(langCode);
          },
          error: () => this.setLanguage("es"),
        });
      },
      error: () => this.setLanguage("es"),
    });
  }

  private isSupported(code: string): boolean {
    return this.languages.some((l) => l.code === code);
  }
}
